import { ErrorState, LoadingState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import {
  SEC_SUMMARY_KEY,
  fetchSecSummary,
  type SecMeasurement,
  type SecSummaryDto,
} from "../../api/sec.js";
import {
  ControlFreshBadge,
  ControlHead,
  ControlMono,
  ControlNote,
  ControlNotMeasurable,
  ControlPanel,
  ControlRow,
  ControlTableWrap,
  MONO_CELL_STYLE,
  TABLE_STYLE,
  TD_STYLE,
  TH_STYLE,
  asList,
  formatInteger,
  measuredNumber,
  type ControlTone,
} from "../control/ControlPrimitives.js";

function isMeasurement(value: unknown): value is SecMeasurement {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as SecMeasurement).status !== undefined &&
    ((value as SecMeasurement).value === null ||
      typeof (value as SecMeasurement).value === "number")
  );
}

function isSecSummary(value: SecSummaryDto | null): value is SecSummaryDto {
  const facts = value?.repositoryFacts;
  return (
    value !== null &&
    typeof value.measuredAt === "string" &&
    isMeasurement(value.activeSessions) &&
    isMeasurement(value.failedLogins24h) &&
    // An unmeasured RLS check is a legitimate answer, not a broken payload:
    // the service keeps the repository rows measured when the database is
    // unreachable, so demanding numbers here would hide exactly those rows.
    typeof value.rlsForce?.status === "string" &&
    (value.rlsForce.total === null || typeof value.rlsForce.total === "number") &&
    (value.rlsForce.forced === null || typeof value.rlsForce.forced === "number") &&
    typeof value.pkce?.method === "string" &&
    facts !== undefined &&
    isMeasurement(facts.supportPasswordRotation) &&
    isMeasurement(facts.sudoersWrapper) &&
    isMeasurement(facts.plaintextSecretsInScripts)
  );
}

/**
 * An instant the payload carries, written in the reader's locale. Never
 * `Date.now()`. The explicit `new Intl.DateTimeFormat(locale, ...)` is the
 * cockpit's formatting pattern: it binds the reader's locale instead of the
 * host's, so an Arabic reader gets Arabic digits and the same two fields
 * (`dateStyle` medium, `timeStyle` short) every other view shows.
 */
function atTime(iso: string | null | undefined, locale: string): string | null {
  if (typeof iso !== "string") return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(
    parsed,
  );
}

/**
 * One repository claim as the sheet prints it.
 *
 * `scope` is the answer to "over what?" and is never decoration: a count of
 * findings without the size of the scan is a number a reader cannot weigh.
 */
interface RepoRow {
  readonly key: string;
  readonly testId: string;
  readonly label: string;
  readonly tone: ControlTone;
  readonly result: ReactNode;
  readonly scope: ReactNode;
}

/**
 * The Security sheet, fed solely by `GET /v1/admin/gtcc/sec/summary`.
 *
 * C-GTCC-VIEW-SEC1. Before this package the sheet printed "nicht messbar" three
 * times over, with the reason `no_ci_evidence_json_injected` — while the
 * evidence it was waiting for was already committed and CI-guarded in
 * `services/api/src/modules/gtcc/sec-evidence/evidence.generated.json` and
 * imported by nobody. Nothing here is a new measurement: the two rows that
 * became numbers are the scan's numbers, carried to the screen.
 *
 * What is still unmeasured stays unmeasured and says why in its own words:
 * `failedLogins24h` (callback failures are logged, never counted) and the
 * support-password rotation (no versioned source records one). Those sentences
 * come from the payload, never from this file — a view that invents a reason is
 * as dishonest as one that invents a number.
 *
 * The head's freshness badge covers the database half; the repository panel
 * carries the scan's OWN timestamp in its note, because a repository fact is
 * exactly as old as the scan and no younger.
 */
export function SecView(): ReactNode {
  const { t, locale } = useLocale();
  const query = useQuery({
    queryKey: SEC_SUMMARY_KEY,
    queryFn: ({ signal }) => fetchSecSummary(signal),
    staleTime: 30_000,
  });
  const result = query.data;
  const summary = result?.ok ? result.data : null;
  const validSummary = isSecSummary(summary);
  const measuredAt = validSummary ? atTime(summary.measuredAt, locale) : null;

  const head = (
    <ControlHead
      testId="gtcc-sec-head"
      title={t("admin.gtcc.sec.sheet.title")}
      subtitle={t("admin.gtcc.sec.sheet.subtitle")}
      fresh={
        measuredAt === null ? undefined : (
          <ControlFreshBadge
            testId="gtcc-sec-fresh"
            text={t("admin.gtcc.sec.sheet.measured_at", { time: measuredAt })}
          />
        )
      }
    />
  );

  if (query.isPending)
    return (
      <Stack gap={3} data-testid="gtcc-view-sec">
        {head}
        <div data-testid="gtcc-sec-loading">
          <LoadingState label={t("admin.gtcc.sec.sessions.loading")} />
        </div>
      </Stack>
    );

  if (query.isError || (result !== undefined && !result.ok))
    return (
      <Stack gap={3} data-testid="gtcc-view-sec">
        {head}
        <div data-testid="gtcc-sec-error">
          <ErrorState
            severity="warn"
            title={
              result && !result.ok && result.kind === "forbidden"
                ? t("admin.gtcc.sec.forbidden")
                : t("admin.gtcc.sec.sessions.error")
            }
          />
        </div>
      </Stack>
    );

  if (!validSummary)
    return (
      <Stack gap={3} data-testid="gtcc-view-sec">
        {head}
        <div role="status">
          <ControlNotMeasurable
            testId="gtcc-sec-unmeasurable"
            label={t("admin.gtcc.sec.not_measurable")}
          />
        </div>
      </Stack>
    );

  const facts = summary.repositoryFacts;
  // The scope fields pass the canonical gates before they are read. A payload
  // that omits them — an older API than this build, or a truncated answer —
  // then costs the scope cell its detail and nothing else; the alternative is
  // a crash where a statement belongs (P10).
  const sudoersCommands = asList(facts.sudoersCommands);
  const sudoersReferenced = measuredNumber(facts.sudoersReferenced);
  const scannedShellFiles = measuredNumber(facts.scannedShellFiles);
  const notMeasurable = (testId: string): ReactNode => (
    <ControlNotMeasurable testId={testId} label={t("admin.gtcc.sec.not_measurable")} />
  );

  // The three repository claims, each one a row of the committed scan result.
  // `status === "measured"` is the only thing that puts a number on screen; a
  // row the scan could not answer keeps its own reason as the scope cell, so
  // "not measurable" never stands there unexplained.
  const repoRows: readonly RepoRow[] = [
    {
      key: "sudoers",
      testId: "gtcc-sec-sudoers",
      label: t("admin.gtcc.sec.sheet.sudoers"),
      tone:
        facts.sudoersWrapper.status !== "measured"
          ? "muted"
          : facts.sudoersWrapper.value === sudoersReferenced
            ? "ok"
            : "warn",
      result:
        facts.sudoersWrapper.status === "measured" && facts.sudoersWrapper.value !== null ? (
          <ControlMono>
            {t("admin.gtcc.sec.sheet.sudoers_value", {
              versioned: formatInteger(facts.sudoersWrapper.value, locale),
              referenced:
                sudoersReferenced === null ? "—" : formatInteger(sudoersReferenced, locale),
            })}
          </ControlMono>
        ) : (
          notMeasurable("gtcc-sec-sudoers-unmeasurable")
        ),
      scope:
        sudoersCommands.length === 0 ? (
          (facts.sudoersWrapper.reason ?? "—")
        ) : (
          <ControlMono>{sudoersCommands.join(", ")}</ControlMono>
        ),
    },
    {
      key: "plaintext",
      testId: "gtcc-sec-plaintext",
      label: t("admin.gtcc.sec.sheet.plaintext"),
      tone:
        facts.plaintextSecretsInScripts.status !== "measured"
          ? "muted"
          : facts.plaintextSecretsInScripts.value === 0
            ? "ok"
            : "crit",
      result:
        facts.plaintextSecretsInScripts.status === "measured" &&
        facts.plaintextSecretsInScripts.value !== null ? (
          <ControlMono>
            {t("admin.gtcc.sec.sheet.findings_value", {
              findings: formatInteger(facts.plaintextSecretsInScripts.value, locale),
            })}
          </ControlMono>
        ) : (
          notMeasurable("gtcc-sec-plaintext-unmeasurable")
        ),
      scope:
        scannedShellFiles === null
          ? (facts.plaintextSecretsInScripts.reason ?? "—")
          : t("admin.gtcc.sec.sheet.scanned_files", {
              files: formatInteger(scannedShellFiles, locale),
            }),
    },
    {
      key: "rotation",
      testId: "gtcc-sec-rotation",
      label: t("admin.gtcc.sec.sheet.rotation"),
      tone: facts.supportPasswordRotation.status === "measured" ? "ok" : "muted",
      result:
        facts.supportPasswordRotation.status === "measured" &&
        facts.supportPasswordRotation.value !== null ? (
          <ControlMono>{formatInteger(facts.supportPasswordRotation.value, locale)}</ControlMono>
        ) : (
          notMeasurable("gtcc-sec-rotation-unmeasurable")
        ),
      // The scan's own sentence about why it cannot answer. Printing it beats
      // printing nothing: an empty cell reads like an oversight, this reads
      // like the finding it is.
      scope: facts.supportPasswordRotation.reason ?? "—",
    },
  ];

  const evidenceAt = atTime(facts.measuredAt, locale);

  return (
    <Stack gap={3} data-testid="gtcc-view-sec">
      {head}
      <ControlPanel testId="gtcc-sec-access" title={t("admin.gtcc.sec.sheet.access")}>
        <ControlRow
          tone={summary.activeSessions.status === "measured" ? "ok" : "muted"}
          label={t("admin.gtcc.sec.sheet.active_sessions")}
          value={
            summary.activeSessions.status === "measured" &&
            summary.activeSessions.value !== null ? (
              <ControlMono>{formatInteger(summary.activeSessions.value, locale)}</ControlMono>
            ) : (
              notMeasurable("gtcc-sec-active-sessions-unmeasurable")
            )
          }
          testId="gtcc-sec-active-sessions"
        />
        <ControlRow
          tone={summary.failedLogins24h.status === "measured" ? "ok" : "muted"}
          label={t("admin.gtcc.sec.sheet.failed_logins")}
          value={
            summary.failedLogins24h.status === "measured" &&
            summary.failedLogins24h.value !== null ? (
              <ControlMono>{formatInteger(summary.failedLogins24h.value, locale)}</ControlMono>
            ) : (
              notMeasurable("gtcc-sec-failed-logins-unmeasurable")
            )
          }
          testId="gtcc-sec-failed-logins"
        />
        <ControlRow
          // A tenant table without FORCE is not a shade of warning: a
          // table-owning role bypasses row security there, so the gap is
          // critical the moment it is one.
          tone={
            summary.rlsForce.status !== "measured"
              ? "muted"
              : summary.rlsForce.forced === summary.rlsForce.total
                ? "ok"
                : "crit"
          }
          label={t("admin.gtcc.sec.sheet.rls")}
          value={
            summary.rlsForce.status === "measured" ? (
              <ControlMono>{`${summary.rlsForce.forced} / ${summary.rlsForce.total} FORCE`}</ControlMono>
            ) : (
              notMeasurable("gtcc-sec-rls-unmeasurable")
            )
          }
          testId="gtcc-sec-rls"
        />
        <ControlRow
          tone="ok"
          label={t("admin.gtcc.sec.sheet.pkce")}
          value={<ControlMono>{`${summary.pkce.method} ✓`}</ControlMono>}
          testId="gtcc-sec-pkce"
        />
        <ControlNote testId="gtcc-sec-pkce-source">{summary.pkce.source}</ControlNote>
      </ControlPanel>
      <ControlPanel testId="gtcc-sec-secrets" title={t("admin.gtcc.sec.sheet.secrets")}>
        <ControlTableWrap testId="gtcc-sec-repo-table">
          <table aria-label={t("admin.gtcc.sec.sheet.secrets")} style={TABLE_STYLE}>
            <thead>
              <tr>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.sec.sheet.column.check")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.sec.sheet.column.result")}
                </th>
                <th scope="col" style={TH_STYLE}>
                  {t("admin.gtcc.sec.sheet.column.scope")}
                </th>
              </tr>
            </thead>
            <tbody>
              {repoRows.map((row) => (
                <tr key={row.key} data-testid={row.testId} data-tone={row.tone}>
                  <td style={TD_STYLE}>{row.label}</td>
                  <td style={MONO_CELL_STYLE}>{row.result}</td>
                  <td style={TD_STYLE}>{row.scope}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ControlTableWrap>
        <ControlNote testId="gtcc-sec-repo-note">
          {evidenceAt === null
            ? t("admin.gtcc.sec.sheet.repository_note")
            : t("admin.gtcc.sec.sheet.evidence_note", {
                time: evidenceAt,
                sources: asList(facts.sources).join(", "),
              })}
        </ControlNote>
      </ControlPanel>
    </Stack>
  );
}
